// Group-by selector for TableView. Default ("phase") preserves the existing
// per-phase rendering. Other modes re-bucket the flat task list into
// phase-like sections (status / priority / assignee).
//
// Persistence: per-project localStorage so each roadmap can have its own
// preferred grouping.

import type { Status, Task, TaskPriority, TaskStatus } from "@juuplan/shared";
import { findUser } from "../store/users";
import {
  TASK_PRIORITY_LABEL,
  TASK_PRIORITY_VAR,
  TASK_STATUS_LABEL,
  TASK_STATUS_VAR,
} from "./format";

export type GroupBy = "phase" | "status" | "priority" | "assignee";

export const GROUP_BY_OPTIONS: { id: GroupBy; label: string }[] = [
  { id: "phase", label: "Phase" },
  { id: "status", label: "Status" },
  { id: "priority", label: "Priorität" },
  { id: "assignee", label: "Zuständig" },
];

const STORAGE_PREFIX = "juuplan:groupby:";

function isGroupBy(v: unknown): v is GroupBy {
  return v === "phase" || v === "status" || v === "priority" || v === "assignee";
}

export function loadGroupBy(projectId: number): GroupBy {
  if (typeof localStorage === "undefined") return "phase";
  const raw = localStorage.getItem(`${STORAGE_PREFIX}${projectId}`);
  return isGroupBy(raw) ? raw : "phase";
}

export function saveGroupBy(projectId: number, gb: GroupBy): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(`${STORAGE_PREFIX}${projectId}`, gb);
}

// ---------- hide-done toggle (per project) ----------

const HIDE_DONE_PREFIX = "juuplan:hide-done:";

/** Whether the TableView should hide completed (status "done") tasks. */
export function loadHideDone(projectId: number): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(`${HIDE_DONE_PREFIX}${projectId}`) === "1";
}

export function saveHideDone(projectId: number, hide: boolean): void {
  if (typeof localStorage === "undefined") return;
  const key = `${HIDE_DONE_PREFIX}${projectId}`;
  // Default (show all) drops the key so we don't accumulate stale entries.
  if (hide) localStorage.setItem(key, "1");
  else localStorage.removeItem(key);
}

// ---------- bucketing ----------

/**
 * A phase-like bucket used by TableView when GroupBy ≠ "phase".
 * `key` is a stable string id (status/priority enum value, "u<userId>", "none");
 * `label` and `color` drive the section header.
 */
export interface Bucket {
  key: string;
  label: string;
  color: string;
  tasks: Task[];
  /** WIP-Limit der Spalte (nur bei Status-Buckets gesetzt); null/undefined = keins. */
  wipLimit?: number | null;
}

const STATUS_ORDER: TaskStatus[] = [
  "backlog",
  "in_progress",
  "review",
  "blocked",
  "done",
];

const PRIORITY_ORDER: (TaskPriority | "none")[] = [
  "urgent",
  "high",
  "medium",
  "low",
  "none",
];

/** Group tasks by status — fixed enum order, always renders all 5 buckets. */
export function groupByStatus(tasks: Task[]): Bucket[] {
  const byStatus = new Map<TaskStatus, Task[]>();
  for (const s of STATUS_ORDER) byStatus.set(s, []);
  for (const t of tasks) {
    const arr = byStatus.get(t.status);
    if (arr) arr.push(t);
  }
  const out: Bucket[] = [];
  for (const s of STATUS_ORDER) {
    const list = byStatus.get(s) ?? [];
    if (list.length === 0) continue;
    out.push({
      key: `status:${s}`,
      label: TASK_STATUS_LABEL[s],
      color: TASK_STATUS_VAR[s],
      tasks: list,
    });
  }
  return out;
}

/** Group tasks by priority — urgent → low, plus an "Ohne Priorität" tail bucket. */
export function groupByPriority(tasks: Task[]): Bucket[] {
  const byPrio = new Map<TaskPriority | "none", Task[]>();
  for (const p of PRIORITY_ORDER) byPrio.set(p, []);
  for (const t of tasks) {
    const key = t.priority ?? "none";
    const arr = byPrio.get(key);
    if (arr) arr.push(t);
  }
  const out: Bucket[] = [];
  for (const p of PRIORITY_ORDER) {
    const list = byPrio.get(p) ?? [];
    if (list.length === 0) continue;
    out.push({
      key: `prio:${p}`,
      label: p === "none" ? "Ohne Priorität" : TASK_PRIORITY_LABEL[p],
      color:
        p === "none"
          ? "var(--color-outline)"
          : TASK_PRIORITY_VAR[p as TaskPriority],
      tasks: list,
    });
  }
  return out;
}

/**
 * Group tasks by primary assignee. "Nicht zugewiesen" comes first, then
 * users sorted by name. Falls back to "User #<id>" if the user cache hasn't
 * resolved yet.
 */
export function groupByAssignee(tasks: Task[]): Bucket[] {
  const unassigned: Task[] = [];
  const byUser = new Map<number, Task[]>();
  for (const t of tasks) {
    const uid = t.primary_assignee_id;
    if (uid === null) {
      unassigned.push(t);
      continue;
    }
    const arr = byUser.get(uid) ?? [];
    arr.push(t);
    byUser.set(uid, arr);
  }
  const userEntries = Array.from(byUser.entries()).map(([id, list]) => {
    const u = findUser(id);
    return { id, name: u?.name ?? `User #${id}`, list };
  });
  userEntries.sort((a, b) =>
    a.name.localeCompare(b.name, "de", { sensitivity: "base" })
  );
  const out: Bucket[] = [];
  if (unassigned.length > 0) {
    out.push({
      key: "user:none",
      label: "Nicht zugewiesen",
      color: "var(--color-outline)",
      tasks: unassigned,
    });
  }
  for (const e of userEntries) {
    out.push({
      key: `user:${e.id}`,
      label: e.name,
      color: "var(--color-primary)",
      tasks: e.list,
    });
  }
  return out;
}

/**
 * Group tasks by dynamic per-project status id.
 * Returns one bucket per status (in sortOrder), always including empty ones.
 * Tasks whose statusInfo.id matches no entry fall into a "_fallback" bucket.
 */
export function groupByStatusId(tasks: Task[], statuses: Status[]): Bucket[] {
  const byId = new Map<number, Task[]>();
  for (const s of statuses) byId.set(s.id, []);
  const fallback: Task[] = [];

  for (const t of tasks) {
    const sid = t.statusInfo?.id;
    if (sid !== undefined && sid !== null && byId.has(sid)) {
      byId.get(sid)!.push(t);
    } else {
      fallback.push(t);
    }
  }

  const out: Bucket[] = [];
  for (const s of statuses) {
    const list = byId.get(s.id) ?? [];
    list.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    out.push({ key: `status-id:${s.id}`, label: s.name, color: s.color, tasks: list, wipLimit: s.wip_limit });
  }
  if (fallback.length > 0) {
    fallback.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    out.push({
      key: "_fallback",
      label: "Sonstiges",
      color: "var(--color-outline)",
      tasks: fallback,
    });
  }
  return out;
}

/**
 * Top-level dispatcher used by TableView.
 *
 * When `statuses` is provided and `gb === "status"`, uses the project's
 * custom statuses via `groupByStatusId` instead of the fixed enum fallback.
 */
export function bucketTasks(
  tasks: Task[],
  gb: GroupBy,
  statuses?: Status[]
): Bucket[] {
  if (gb === "status") {
    if (statuses && statuses.length > 0) return groupByStatusId(tasks, statuses);
    return groupByStatus(tasks);
  }
  if (gb === "priority") return groupByPriority(tasks);
  if (gb === "assignee") return groupByAssignee(tasks);
  return [];
}
